import { createSelector } from '@reduxjs/toolkit'
import type { RootState } from '../index'
import { TableStatus } from '../../types'
import type { TableType } from '../../types/table.types'
import { orderAvailableTableTypes } from '../../utils/bookingTimelineUtils'

// Basic selectors
export const selectTables = (state: RootState) => [...state.tables.tables].sort((a, b) => a.id - b.id)
export const selectTablesLoading = (state: RootState) => state.tables.tablesLoading
export const selectTablesError = (state: RootState) => state.tables.tablesError
export const selectSelectedTableId = (state: RootState) => state.tables.selectedTableId
export const selectTableDetailsCache = (state: RootState) => state.tables.tableDetailsCache
export const selectLastFetch = (state: RootState) => state.tables.lastFetch

// Memoized selectors
export const selectActiveTables = createSelector(
    [selectTables],
    (tables) => tables.filter(t => t.status === TableStatus.ACTIVE)
)

export const selectPausedTables = createSelector(
    [selectTables],
    (tables) => tables.filter(t => t.status === TableStatus.PAUSED)
)

export const selectIdleTables = createSelector(
    [selectTables],
    (tables) => tables.filter(t => t.status === TableStatus.IDLE)
)

export const selectBookedTables = createSelector(
    [selectTables],
    (tables) => tables.filter(t => t.status === TableStatus.BOOKED)
)

export const selectMaintenanceTables = createSelector(
    [selectTables],
    (tables) => tables.filter(t =>
        t.status === TableStatus.MAINTENANCE ||
        t.status === TableStatus.OFFLINE ||
        t.status === TableStatus.ERROR
    )
)

export const selectUnhealthyTables = createSelector(
    [selectTables],
    (tables) => tables.filter(t => t.missedHeartbeats > 5)
)

// Tables grouped by type
export const selectTablesByType = createSelector(
    [selectTables],
    (tables) => {
        return tables.reduce((acc, table) => {
            if (!acc[table.type]) {
                acc[table.type] = []
            }
            acc[table.type].push(table)
            return acc
        }, {} as Record<string, typeof tables>)
    }
)

export const selectAvailableTableTypes = createSelector(
    [selectTables],
    (tables): TableType[] => orderAvailableTableTypes(tables),
)

// Tables grouped by status
export const selectTablesByStatus = createSelector(
    [selectTables],
    (tables) => {
        return tables.reduce((acc, table) => {
            if (!acc[table.status]) {
                acc[table.status] = []
            }
            acc[table.status].push(table)
            return acc
        }, {} as Record<TableStatus, typeof tables>)
    }
)

// Parametrized selectors (use factory pattern)
export const selectTableById = (tableId: number) =>
    createSelector(
        [selectTables, selectTableDetailsCache],
        (tables, cache) => {
            // Prefer cached version with session data
            if (cache[tableId]) {
                return cache[tableId].table
            }
            // Fallback to tables list
            return tables.find(t => t.id === tableId)
        }
    )

export const selectTableWithSession = (tableId: number) =>
    createSelector(
        [selectTableDetailsCache],
        (cache) => cache[tableId]
    )

export const selectSelectedTable = createSelector(
    [selectSelectedTableId, selectTables, selectTableDetailsCache],
    (selectedId, tables, cache) => {
        if (!selectedId) return null

        // Prefer cached version
        if (cache[selectedId]) {
            return cache[selectedId].table
        }
        return tables.find(t => t.id === selectedId) || null
    }
)

export const selectSelectedTableWithSession = createSelector(
    [selectSelectedTableId, selectTableDetailsCache],
    (selectedId, cache) => {
        if (!selectedId) return null
        return cache[selectedId] || null
    }
)

// Statistics selectors
export const selectTableStats = createSelector(
    [selectTables],
    (tables) => ({
        total: tables.length,
        active: tables.filter(t => t.status === TableStatus.ACTIVE).length,
        paused: tables.filter(t => t.status === TableStatus.PAUSED).length,
        idle: tables.filter(t => t.status === TableStatus.IDLE).length,
        booked: tables.filter(t => t.status === TableStatus.BOOKED).length,
        maintenance: tables.filter(t =>
            t.status === TableStatus.MAINTENANCE ||
            t.status === TableStatus.OFFLINE ||
            t.status === TableStatus.ERROR
        ).length,
        unhealthy: tables.filter(t => t.missedHeartbeats > 5).length
    })
)

// Check if data needs refresh (older than 10 seconds)
export const selectNeedsRefresh = createSelector(
    [selectLastFetch],
    (lastFetch) => {
        if (!lastFetch) return true
        return Date.now() - lastFetch > 10000 // 10 seconds
    }
)